import { checkTelValidity } from '@opendoor/bricks/core/Input/helpers';
import { clientFromUrl } from 'setupClient';
import { assign, createMachine, InterpreterFrom, StateFrom } from 'xstate';

import { OdProtosWebData_ListingOfferDetails_FinancingStatus } from '__generated__/globalTypes';

import { FundingSource as FundingSourceEnum } from 'buying/components/buyDirectCheckout/commonTypes';
import { CreateBuyDirectListingOffer } from 'buying/graphql/mutations';

import { FileUpload } from 'sellDirect/helpers/FileUploadClient';

const apolloClient = clientFromUrl();

/**
 * This is the shape of the contact details object that is passed around the state machine
 */
export type ContactDetail = {
  id: string;
  name?: string;
  email?: string;
  emailVerification?: string;
  phone?: string;
};

export type ContactDetails = Record<string, ContactDetail>;

/**
 * This is the state machine's context config
 */
export interface BuyDirectCheckoutFlowContext {
  addressId?: string;
  buyDirectPrice?: number;
  closeDate?: string;
  closingCosts?: number;
  contingentSale?: 'yes' | 'no';
  contactDetails?: Record<string, ContactDetail>; // buyer, co-buyer
  contractSignatureId?: string;
  errorMessage?: string;
  fundingSource?: FundingSourceEnum;
  hasMultipleOffers: boolean;
  hasVerbalAgreement: boolean;
  isBuyDirectEligible: boolean;
  isInContract: boolean;
  listingOfferId?: string;
  listPrice?: number;
  loanAmount?: number;
  mortgageDownPaymentAmount?: number;
  mortgageDownPaymentPercentage?: number;
  mortgagePreapproval?: FileUpload;
  ownAgent?: 'yes' | 'no';
}

/**
 * These define the different nodes of the state machine
 */
export enum CheckoutState {
  Idle = 'IDLE',
  ReviewDetails = 'REVIEW_DETAILS',
  BackupOfferDetails = 'BACKUP_OFFER_DETAILS',
  OfferDeadline = 'OFFER_DEADLINE',
  InContract = 'IN_CONTRACT',
  ContactDetails = 'CONTACT_DETAILS',
  Confirmation = 'CONFIRMATION',
  FundingSource = 'FUNDING_SOURCE',
  PreApproval = 'PRE_APPROVAL',
  PreApprovalConfirmation = 'PRE_APPROVAL_CONFIRMATION',
  ReviewContractDetails = 'REVIEW_CONTRACT_DETAILS',
  OwnAgent = 'OWN_AGENT',
  ContingentSale = 'CONTINGENT_SALE',
  Redirect = 'REDIRECT',
  CreateBuyDirectListingOffer = 'CREATE_BUY_DIRECT_LISTING_OFFER',
  CreateBuyDirectBackupOffer = 'CREATE_BUY_DIRECT_BACKUP_OFFER',
  CreateBuyDirectListingOfferDeadline = 'CREATE_BUY_DIRECT_LISTING_OFFER_DEADLINE',
}

const FundingSourceEnumToProtoEnum = {
  [FundingSourceEnum.Cash]: OdProtosWebData_ListingOfferDetails_FinancingStatus.CASH,
  [FundingSourceEnum.Conventional]:
    OdProtosWebData_ListingOfferDetails_FinancingStatus.CONVENTIONAL,
  [FundingSourceEnum.FHA]: OdProtosWebData_ListingOfferDetails_FinancingStatus.FHA_LOAN,
  [FundingSourceEnum.VA]: OdProtosWebData_ListingOfferDetails_FinancingStatus.VA_LOAN,
  [FundingSourceEnum.USDA]: OdProtosWebData_ListingOfferDetails_FinancingStatus.USDA_LOAN,
};

const PRIMARY_BUYER_KEY = '0';
const COBUYER_KEY = '1';

// Validate all contact details in order over precedence
export const validateContactDetails = (contactDetails: ContactDetails): string | null => {
  let error;
  if (!contactDetails[PRIMARY_BUYER_KEY]?.name) {
    error = 'Please enter a name for the primary buyer';
  } else if (
    !contactDetails[PRIMARY_BUYER_KEY]?.phone ||
    !checkTelValidity(contactDetails[PRIMARY_BUYER_KEY]?.phone)
  ) {
    error = 'Please enter a valid phone number for the primary buyer';
  } else if (!contactDetails[PRIMARY_BUYER_KEY]?.email) {
    error = 'Please enter an email for the primary buyer';
  } else if (
    contactDetails[PRIMARY_BUYER_KEY]?.email !==
    contactDetails[PRIMARY_BUYER_KEY]?.emailVerification
  ) {
    error = 'Primary buyer emails do not match';
  } else {
    error = null;
  }
  if (contactDetails[COBUYER_KEY]?.name && !error) {
    if (
      !contactDetails[COBUYER_KEY]?.phone ||
      !checkTelValidity(contactDetails[COBUYER_KEY]?.phone)
    ) {
      error = 'Please enter a valid phone number for the co-buyer';
    } else if (!contactDetails[COBUYER_KEY]?.email) {
      error = 'Please enter an email for the co-buyer';
    } else if (
      contactDetails[COBUYER_KEY].email !== contactDetails[COBUYER_KEY]?.emailVerification
    ) {
      error = 'Co-buyer emails do not match';
    } else if (contactDetails[COBUYER_KEY].email === contactDetails[PRIMARY_BUYER_KEY].email) {
      error = 'Co-buyer email cannot be the same as primary buyer email';
    } else {
      error = null;
    }
  }
  return error;
};

/**
 * This function creates a Buy Direct listing offer in any resale scenario, including "highest and best"
 */
const createBuyDirectListingOffer = async (context: BuyDirectCheckoutFlowContext) => {
  const closeOfEscrowDate = context?.closeDate ? new Date(context?.closeDate) : null;
  const downPaymentCents = context?.mortgageDownPaymentAmount
    ? Math.round(context.mortgageDownPaymentAmount * 100)
    : null;
  const closeOfEscrow = closeOfEscrowDate
    ? {
        year: closeOfEscrowDate.getFullYear(),
        month: closeOfEscrowDate.getMonth() + 1, // getMonth is 0th indexed
        day: closeOfEscrowDate.getDate(),
      }
    : null;

  const preApprovalLetter = context?.mortgagePreapproval?.getUrl
    ? {
        name: context?.mortgagePreapproval?.file?.name,
        s3Path: context?.mortgagePreapproval?.getUrl, // leave the AWS presigned auth query params intact for the backend to handle
      }
    : null;

  const financingType = context?.fundingSource
    ? FundingSourceEnumToProtoEnum?.[context?.fundingSource]
    : OdProtosWebData_ListingOfferDetails_FinancingStatus.DONT_KNOW;

  return await apolloClient.mutate({
    mutation: CreateBuyDirectListingOffer,
    variables: {
      buyer: {
        email: context?.contactDetails?.[PRIMARY_BUYER_KEY]?.email,
        name: context?.contactDetails?.[PRIMARY_BUYER_KEY]?.name,
        phoneNumber: context?.contactDetails?.[PRIMARY_BUYER_KEY]?.phone,
      },
      closeOfEscrow,
      coBuyer: {
        email: context?.contactDetails?.[COBUYER_KEY]?.email,
        name: context?.contactDetails?.[COBUYER_KEY]?.name,
        phoneNumber: context?.contactDetails?.[COBUYER_KEY]?.phone,
      },
      downPaymentCents,
      financingType,
      listing: {
        addressToken: context?.addressId,
      },
      preApprovalLetter,
    },
  });
};

/**
 * This is the Buy Direct checkout state machine configuration
 */
export const BuyDirectCheckoutFlowStateMachine = createMachine<BuyDirectCheckoutFlowContext>(
  {
    context: {
      addressId: undefined,
      hasMultipleOffers: false,
      hasVerbalAgreement: false,
      isBuyDirectEligible: true,
      isInContract: false,
    },
    schema: {
      events: {} as
        | { type: 'setAddressId'; value: string }
        | { type: 'setBuyDirectPrice'; value: number }
        | { type: 'setCloseDate'; value: string }
        | { type: 'setClosingCosts'; value: number }
        | { type: 'setContingentSale'; value: string }
        | { type: 'setContactDetails'; value: ContactDetails }
        | { type: 'setFundingSource'; value: string }
        | { type: 'setHasMultipleOffers'; value: boolean }
        | { type: 'setHasVerbalAgreement'; value: boolean }
        | { type: 'setIsBuyDirectEligible'; value: boolean }
        | { type: 'setIsInContract'; value: boolean }
        | { type: 'setListPrice'; value: number }
        | { type: 'setLoanAmount'; value: number }
        | { type: 'setMortgageDownPaymentAmount'; value: number }
        | { type: 'setMortgageDownPaymentPercentage'; value: number }
        | { type: 'setMortgagePreapproval'; value: FileUpload }
        | { type: 'setOwnAgent'; value: string }
        | { type: 'setErrorMessage'; value: string },
    },
    id: 'buy-direct-checkout-flow',
    initial: 'IDLE',
    states: {
      [CheckoutState.Idle]: {
        on: {
          FORWARD: [
            {
              target: CheckoutState.ReviewDetails,
              cond: (context) =>
                context.addressId !== undefined &&
                context.hasMultipleOffers === false &&
                context.hasVerbalAgreement === false &&
                context.isBuyDirectEligible === true &&
                context.isInContract === false,
            },
            {
              target: CheckoutState.BackupOfferDetails,
              cond: (context) =>
                context.addressId !== undefined &&
                context.hasMultipleOffers === false &&
                context.hasVerbalAgreement === true &&
                context.isBuyDirectEligible === true &&
                context.isInContract === false,
            },
            {
              target: CheckoutState.OfferDeadline,
              cond: (context) =>
                context.addressId !== undefined &&
                context.hasMultipleOffers === true &&
                context.hasVerbalAgreement === false &&
                context.isBuyDirectEligible === true &&
                context.isInContract === false,
            },
            {
              target: CheckoutState.InContract,
              cond: (context) => context.addressId !== undefined && context.isInContract === true,
            },
            {
              target: CheckoutState.Redirect,
              cond: (context) =>
                context.addressId === undefined || context.isBuyDirectEligible === false,
            },
          ],
          setAddressId: {
            actions: 'setAddressId',
          },
          setBuyDirectPrice: {
            actions: 'setBuyDirectPrice',
          },
          setClosingCosts: {
            actions: 'setClosingCosts',
          },
          setHasMultipleOffers: {
            actions: 'setHasMultipleOffers',
          },
          setHasVerbalAgreement: {
            actions: 'setHasVerbalAgreement',
          },
          setIsBuyDirectEligible: {
            actions: 'setIsBuyDirectEligible',
          },
          setIsInContract: {
            actions: 'setIsInContract',
          },
          setListPrice: {
            actions: 'setListPrice',
          },
          setContactDetails: {
            actions: 'setContactDetails',
          },
        },
      },
      [CheckoutState.ReviewDetails]: {
        on: {
          FORWARD: [
            {
              target: CheckoutState.ContactDetails,
            },
          ],
          setErrorMessage: {
            actions: 'setErrorMessage',
          },
          setContactDetails: {
            actions: 'setContactDetails',
          },
        },
      },
      [CheckoutState.BackupOfferDetails]: {
        on: {
          FORWARD: {
            target: CheckoutState.CreateBuyDirectBackupOffer,
            cond: (context) =>
              context.contactDetails !== undefined &&
              validateContactDetails(context.contactDetails) === null,
          },
          BACK: {
            target: CheckoutState.ReviewDetails,
          },
          setContactDetails: {
            actions: 'setContactDetails',
          },
        },
      },
      [CheckoutState.OfferDeadline]: {
        on: {
          FORWARD: {
            target: CheckoutState.CreateBuyDirectListingOfferDeadline,
            cond: (context) =>
              context.contactDetails !== undefined &&
              validateContactDetails(context.contactDetails) === null,
          },
          setContactDetails: {
            actions: 'setContactDetails',
          },
        },
      },
      [CheckoutState.Confirmation]: {
        on: {
          FORWARD: {
            target: CheckoutState.Redirect,
          },
        },
      },
      [CheckoutState.InContract]: {
        on: {
          FORWARD: {
            target: CheckoutState.CreateBuyDirectBackupOffer,
            cond: (context) =>
              context.contactDetails !== undefined &&
              validateContactDetails(context.contactDetails) === null,
          },
          setContactDetails: {
            actions: 'setContactDetails',
          },
        },
      },
      [CheckoutState.ContactDetails]: {
        on: {
          BACK: {
            target: CheckoutState.ReviewDetails,
          },
          FORWARD: [
            {
              target: CheckoutState.FundingSource,
              cond: (context) =>
                context.contactDetails !== undefined &&
                validateContactDetails(context.contactDetails) === null &&
                context.ownAgent === 'no' &&
                context.contingentSale === 'no',
            },
            {
              target: CheckoutState.OwnAgent,
              cond: (context) => context.ownAgent === 'yes',
            },
            {
              target: CheckoutState.ContingentSale,
              cond: (context) => context.contingentSale === 'yes',
            },
          ],
          setContactDetails: {
            actions: 'setContactDetails',
          },
          setOwnAgent: {
            actions: 'setOwnAgent',
          },
          setContingentSale: {
            actions: 'setContingentSale',
          },
        },
      },
      [CheckoutState.FundingSource]: {
        on: {
          BACK: {
            target: CheckoutState.ContactDetails,
          },
          FORWARD: [
            {
              target: CheckoutState.PreApproval,
              cond: (context) =>
                context.fundingSource !== undefined &&
                context.fundingSource !== 'CASH' &&
                context.mortgageDownPaymentAmount !== undefined &&
                context.mortgageDownPaymentPercentage !== undefined &&
                context.closeDate !== undefined &&
                context.loanAmount !== undefined,
            },
            {
              target: CheckoutState.ReviewContractDetails,
              cond: (context) =>
                context.fundingSource !== undefined &&
                context.fundingSource === 'CASH' &&
                context.closeDate !== undefined,
            },
          ],
          setFundingSource: {
            actions: 'setFundingSource',
          },
          setMortgageDownPaymentAmount: {
            actions: 'setMortgageDownPaymentAmount',
          },
          setMortgageDownPaymentPercentage: {
            actions: 'setMortgageDownPaymentPercentage',
          },
          setCloseDate: {
            actions: 'setCloseDate',
          },
          setLoanAmount: {
            actions: 'setLoanAmount',
          },
        },
      },
      [CheckoutState.OwnAgent]: {
        type: 'final',
      },
      [CheckoutState.ContingentSale]: {
        type: 'final',
      },
      [CheckoutState.PreApproval]: {
        on: {
          BACK: {
            target: CheckoutState.FundingSource,
          },
          FORWARD: [
            {
              target: CheckoutState.PreApprovalConfirmation,
              cond: (context) => context.mortgagePreapproval !== undefined,
            },
          ],
          setMortgagePreapproval: {
            actions: 'setMortgagePreapproval',
          },
        },
      },
      [CheckoutState.PreApprovalConfirmation]: {
        on: {
          BACK: {
            target: CheckoutState.FundingSource,
          },
          FORWARD: [
            {
              target: CheckoutState.ReviewContractDetails,
              cond: (context) => context.mortgagePreapproval !== undefined,
            },
          ],
          setMortgagePreapproval: {
            actions: 'setMortgagePreapproval',
          },
        },
      },
      [CheckoutState.ReviewContractDetails]: {
        on: {
          FORWARD: {
            target: CheckoutState.CreateBuyDirectListingOffer,
          },
          BACK: [
            {
              cond: (context) => context?.fundingSource === FundingSourceEnum.Cash,
              target: CheckoutState.FundingSource,
            },
            {
              cond: (context) => context?.fundingSource !== FundingSourceEnum.Cash,
              target: CheckoutState.PreApproval,
            },
          ],
        },
      },
      [CheckoutState.CreateBuyDirectListingOffer]: {
        invoke: {
          id: 'create-buy-direct-listing-offer',
          src: 'createBuyDirectListingOffer',
          onDone: {
            target: CheckoutState.Redirect,
            actions: 'handleCreateOfferResponse',
          },
          onError: {
            target: CheckoutState.ReviewDetails,
            actions: 'handleCreateOfferError',
          },
        },
      },
      [CheckoutState.CreateBuyDirectBackupOffer]: {
        invoke: {
          id: 'create-buy-direct-backup-listing-offer',
          src: 'createBuyDirectListingOffer',
          onDone: {
            target: CheckoutState.Confirmation,
            actions: 'handleCreateOfferResponse',
          },
          onError: {
            target: CheckoutState.ReviewDetails,
            actions: 'handleCreateOfferError',
          },
        },
      },
      [CheckoutState.CreateBuyDirectListingOfferDeadline]: {
        invoke: {
          id: 'create-buy-direct-listing-offer-deadline',
          src: 'createBuyDirectListingOffer',
          onDone: {
            target: CheckoutState.Confirmation,
            actions: 'handleCreateOfferResponse',
          },
          onError: {
            target: CheckoutState.ReviewDetails,
            actions: 'handleCreateOfferError',
          },
        },
      },
      [CheckoutState.Redirect]: {
        type: 'final',
      },
    },
  },
  {
    actions: {
      setAddressId: assign({
        addressId: (_, event) => event.value,
      }),
      setBuyDirectPrice: assign({
        buyDirectPrice: (_, event) => event.value,
      }),
      setCloseDate: assign({
        closeDate: (_, event) => event.value,
      }),
      setClosingCosts: assign({
        closingCosts: (_, event) => event.value,
      }),
      setContingentSale: assign({
        contingentSale: (_, event) => event.value,
      }),
      setContactDetails: assign({
        contactDetails: (_, event) => {
          const buyers: Record<string, ContactDetail> = {};
          const contactDetails: Array<ContactDetail> = Object.values(event.value);
          contactDetails.map((contact: ContactDetail) => {
            buyers[contact.id] = contact;
          });
          return buyers;
        },
      }),
      setFundingSource: assign({
        fundingSource: (_, event) => event.value,
      }),
      setHasMultipleOffers: assign({
        hasMultipleOffers: (_, event) => event.value,
      }),
      setHasVerbalAgreement: assign({
        hasVerbalAgreement: (_, event) => event.value,
      }),
      setIsBuyDirectEligible: assign({
        isBuyDirectEligible: (_, event) => event.value,
      }),
      setIsInContract: assign({
        isInContract: (_, event) => event.value,
      }),
      setListPrice: assign({
        listPrice: (_, event) => event.value,
      }),
      setLoanAmount: assign({
        loanAmount: (_, event) => event.value,
      }),
      setMortgageDownPaymentAmount: assign({
        mortgageDownPaymentAmount: (_, event) => event.value,
      }),
      setMortgageDownPaymentPercentage: assign({
        mortgageDownPaymentPercentage: (_, event) => event.value,
      }),
      setMortgagePreapproval: assign({
        mortgagePreapproval: (_, event) => event.value,
      }),
      setOwnAgent: assign({
        ownAgent: (_, event) => event.value,
      }),
      setErrorMessage: assign({
        errorMessage: (_, event) => event.value,
      }),
      handleCreateOfferResponse: assign((_, event) => {
        return {
          errorMessage: event.data?.data?.errors?.[0]?.message,
          listingOfferId:
            event?.data?.data?.webBuyDirectListingOffer?.createBuyDirectOffer?.listingOfferId,
          contractSignatureId:
            event?.data?.data?.webBuyDirectListingOffer?.createBuyDirectOffer?.contractSignatureId,
        };
      }),
      handleCreateOfferError: assign({
        errorMessage: (_, event) => event?.data?.data?.errors[0].message ?? event?.data?.message,
      }),
    },
    services: {
      createBuyDirectListingOffer,
    },
  },
);

/**
 * These are the types that are exported from this file
 */
export type BuyDirectCheckoutFlowStateMachineState = StateFrom<
  typeof BuyDirectCheckoutFlowStateMachine
>;
export type BuyDirectCheckoutFlowStateMachineSend = InterpreterFrom<
  typeof BuyDirectCheckoutFlowStateMachine
>['send'];
